import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  GraduationCap, DollarSign, Users, Globe, Heart, Lightbulb,
  TrendingUp, ArrowRight, Briefcase, Home, Plane, Target, Sparkles
} from 'lucide-react';

const WhyCollegeMatters = () => {
  const lifetimeEarnings = [
    { degree: "High School Diploma", amount: "$1.3M", color: "bg-gray-400" },
    { degree: "Some College", amount: "$1.5M", color: "bg-gray-500" },
    { degree: "Associate's Degree", amount: "$1.7M", color: "bg-blue-400" },
    { degree: "Bachelor's Degree", amount: "$2.3M", color: "bg-emerald-500" },
    { degree: "Master's Degree", amount: "$2.7M", color: "bg-teal-500" },
    { degree: "Doctoral Degree", amount: "$3.3M", color: "bg-cyan-500" },
    { degree: "Professional Degree", amount: "$3.6M", color: "bg-indigo-500" }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 text-white py-20">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <GraduationCap className="h-16 w-16 mx-auto mb-6 opacity-90" />
          <h1 className="text-4xl md:text-5xl font-bold mb-6">Why College Matters</h1>
          <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto">
            College is worth it, because <span className="font-bold">you</span> are worth it.
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-gradient-to-b from-emerald-50 to-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <p className="text-xl text-gray-700 leading-relaxed">
              College isn't just a place to learn. It's where you'll find a community filled with new people 
              and have new experiences. It's where you'll make friends for life and learn important lessons 
              about yourself. It's where you'll go to reach new heights.
            </p>
          </div>
        </div>
      </section>

      {/* Financial Impact Section */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-100 rounded-full mb-4">
              <DollarSign className="h-8 w-8 text-emerald-600" />
            </div>
            <h2 className="text-3xl font-bold mb-4">Financial Impact</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Whatever your goals — getting a degree, owning a home, traveling the world — you can attain them. 
              They require dedication and focus, but also financial stability. Here's how a college degree can help over a lifetime:
            </p>
          </div>

          <div className="max-w-3xl mx-auto">
            <Card>
              <CardContent className="p-8">
                <h3 className="text-xl font-semibold mb-6 text-center">Lifetime Earnings by Education Level</h3>
                <div className="space-y-4">
                  {lifetimeEarnings.map((item, index) => (
                    <div key={index} className="flex items-center gap-4">
                      <div className="w-40 text-sm text-gray-600">{item.degree}</div>
                      <div className="flex-1 bg-gray-100 rounded-full h-8 overflow-hidden">
                        <div 
                          className={`h-full ${item.color} rounded-full flex items-center justify-end pr-3`}
                          style={{ width: `${(parseFloat(item.amount.replace('$', '').replace('M', '')) / 3.6) * 100}%` }}
                        >
                          <span className="text-white font-bold text-sm">{item.amount}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-6 text-center">
                  Source: U.S. Bureau of Labor Statistics, lifetime earnings estimates
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mt-12">
            <Card className="text-center p-6">
              <Home className="h-10 w-10 text-emerald-600 mx-auto mb-4" />
              <h4 className="font-semibold mb-2">Home Ownership</h4>
              <p className="text-gray-600 text-sm">College graduates are 1.5x more likely to own a home</p>
            </Card>
            <Card className="text-center p-6">
              <Briefcase className="h-10 w-10 text-teal-600 mx-auto mb-4" />
              <h4 className="font-semibold mb-2">Job Security</h4>
              <p className="text-gray-600 text-sm">Lower unemployment rates for degree holders</p>
            </Card>
            <Card className="text-center p-6">
              <TrendingUp className="h-10 w-10 text-cyan-600 mx-auto mb-4" />
              <h4 className="font-semibold mb-2">Career Growth</h4>
              <p className="text-gray-600 text-sm">More opportunities for advancement and promotions</p>
            </Card>
          </div>
        </div>
      </section>

      {/* Personality Development */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
                <Heart className="h-8 w-8 text-purple-600" />
              </div>
              <h2 className="text-3xl font-bold mb-4">Personal Growth & Development</h2>
              <p className="text-gray-600 mb-6">
                College is a transformative experience that shapes who you become. It's not just about academics—it's 
                about discovering yourself, building character, and developing the skills to navigate life's challenges.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <Sparkles className="h-5 w-5 text-purple-500 mt-1" />
                  <div>
                    <strong>Self-Discovery:</strong> Explore your interests, values, and passions in a supportive environment
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Sparkles className="h-5 w-5 text-purple-500 mt-1" />
                  <div>
                    <strong>Independence:</strong> Learn to manage your time, finances, and responsibilities
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Sparkles className="h-5 w-5 text-purple-500 mt-1" />
                  <div>
                    <strong>Confidence:</strong> Develop public speaking, leadership, and problem-solving skills
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Sparkles className="h-5 w-5 text-purple-500 mt-1" />
                  <div>
                    <strong>Resilience:</strong> Learn to overcome challenges and bounce back from setbacks
                  </div>
                </li>
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Card className="p-6 bg-purple-50">
                <Lightbulb className="h-8 w-8 text-purple-600 mb-3" />
                <h4 className="font-semibold mb-2">Critical Thinking</h4>
                <p className="text-sm text-gray-600">Analyze complex problems and develop innovative solutions</p>
              </Card>
              <Card className="p-6 bg-pink-50">
                <Users className="h-8 w-8 text-pink-600 mb-3" />
                <h4 className="font-semibold mb-2">Communication</h4>
                <p className="text-sm text-gray-600">Express ideas clearly in writing and speech</p>
              </Card>
              <Card className="p-6 bg-indigo-50">
                <Target className="h-8 w-8 text-indigo-600 mb-3" />
                <h4 className="font-semibold mb-2">Goal Setting</h4>
                <p className="text-sm text-gray-600">Plan for the future and work toward your dreams</p>
              </Card>
              <Card className="p-6 bg-blue-50">
                <Heart className="h-8 w-8 text-blue-600 mb-3" />
                <h4 className="font-semibold mb-2">Emotional Intelligence</h4>
                <p className="text-sm text-gray-600">Understand and manage your emotions effectively</p>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Exposure & Opportunities */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-100 rounded-full mb-4">
              <Plane className="h-8 w-8 text-teal-600" />
            </div>
            <h2 className="text-3xl font-bold mb-4">Exposure & Opportunities</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              This is your opportunity to take what you're already interested in and pursue it at a deeper level. 
              Explore without the structured limitations of high school.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="p-6 hover:shadow-lg transition-shadow">
              <div className="text-4xl font-bold text-emerald-600 mb-2">100+</div>
              <h4 className="font-semibold mb-2">Majors & Programs</h4>
              <p className="text-sm text-gray-600">Discover fields you never knew existed</p>
            </Card>
            <Card className="p-6 hover:shadow-lg transition-shadow">
              <div className="text-4xl font-bold text-teal-600 mb-2">500+</div>
              <h4 className="font-semibold mb-2">Student Organizations</h4>
              <p className="text-sm text-gray-600">Find your community and make lifelong friends</p>
            </Card>
            <Card className="p-6 hover:shadow-lg transition-shadow">
              <div className="text-4xl font-bold text-cyan-600 mb-2">1000s</div>
              <h4 className="font-semibold mb-2">Internship Opportunities</h4>
              <p className="text-sm text-gray-600">Gain real-world experience before graduating</p>
            </Card>
            <Card className="p-6 hover:shadow-lg transition-shadow">
              <div className="text-4xl font-bold text-blue-600 mb-2">Global</div>
              <h4 className="font-semibold mb-2">Study Abroad Programs</h4>
              <p className="text-sm text-gray-600">Experience different cultures firsthand</p>
            </Card>
          </div>
        </div>
      </section>

      {/* Diversity Section */}
      <section className="py-16 bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <Globe className="h-12 w-12 mb-6 opacity-90" />
              <h2 className="text-3xl font-bold mb-4">Embrace Diversity</h2>
              <p className="text-white/90 mb-6">
                Meet people from various backgrounds and cultures who have interests similar to — and different from — your own. 
                They might even introduce you to new interests you didn't know you had.
              </p>
              <ul className="space-y-3 text-white/90">
                <li className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-white rounded-full" />
                  Learn from diverse perspectives and worldviews
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-white rounded-full" />
                  Build a global network of friends and colleagues
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-white rounded-full" />
                  Develop cultural competence for the global workplace
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-white rounded-full" />
                  Challenge your assumptions and grow as a person
                </li>
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
                <div className="text-4xl font-bold">150+</div>
                <div className="text-white/80">Countries Represented</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
                <div className="text-4xl font-bold">50+</div>
                <div className="text-white/80">Languages Spoken</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
                <div className="text-4xl font-bold">100s</div>
                <div className="text-white/80">Cultural Events</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
                <div className="text-4xl font-bold">∞</div>
                <div className="text-white/80">New Perspectives</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Begin Your Journey?</h2>
          <p className="text-gray-600 mb-8">
            The future may seem uncertain, but college will help you discover the career path that matches 
            your interests and motivates you to become the best version of yourself.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/signup">
              <Button size="lg" className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-8">
                Start Your Application
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/paying-for-college">
              <Button size="lg" variant="outline" className="px-8">
                Learn About Financial Aid
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhyCollegeMatters;
